import { type FC } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";

import { TERM_ID_QUERY_PARAM } from "components/constants";
import { t } from "i18n/i18n";
import { FormField } from "mds/components/FormField";
import { TextField } from "mds/components/TextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { storeApi } from "store/index";
import { toastSuccessMessage } from "utils/alerts";
import { useQueryParamsActions } from "utils/routing";

type AddTermModalProps = {
  eventName?: string;
  eventProperties?: Record<string, unknown>;
  localTerms: Array<{ order?: number }>;
  onClose: () => void;
};

type AddTermFormValues = {
  name: string;
};

export const AddTermModal: FC<AddTermModalProps> = ({
  eventName,
  eventProperties,
  localTerms,
  onClose,
}) => {
  const form = useForm<AddTermFormValues>({
    defaultValues: { name: "" },
  });
  const { setQueryParams } = useQueryParamsActions();

  const onSubmit: SubmitHandler<AddTermFormValues> = ({ name: submittedName }) => {
    const newOrder = (localTerms[localTerms.length - 1]?.order || 0) + 1;
    storeApi.terms
      .create({
        order: newOrder,
        title: submittedName.trim(),
      })
      .then((newItem) => {
        toastSuccessMessage(t("home.toasts.term_added"));
        setQueryParams({ [TERM_ID_QUERY_PARAM]: newItem.id });
        onClose();
      });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormField label={t("nav_list.new_title_term")} name="name" required>
          {(controlProps) => (
            <TextField
              {...form.register("name", {
                required: t("error.field_required"),
                validate: (value) => value.trim().length > 0 || t("error.field_required"),
              })}
              {...controlProps}
              eventName={eventName}
              eventProperties={eventProperties}
              placeholder={t("home.placeholder.term_name")}
            />
          )}
        </FormField>

        <div className="mt-8 flex w-full justify-end gap-2">
          <SquareButton kind="tertiary" size="m" title={t("common.cancel")} onClick={onClose}>
            {t("common.cancel")}
          </SquareButton>

          <SquareButton kind="accent" size="m" title={t("common.add.term")} type="submit">
            {t("common.add.term")}
          </SquareButton>
        </div>
      </form>
    </FormProvider>
  );
};
